import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';

import {
  useEditingIngredientStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  AccommodationProductType,
  IngredientId,
  StarsRatingFilter,
} from '@libs/trip-planner-interfaces';
import {
  BoardBasisCode,
  BoardBasisFilter,
  AmenitiesFilter,
  AccommodationType,
  AccommodationRule,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';
import type { AppMultiSelectOption } from '@libs/ui';

import { HotelSelectionEventType } from '../../../pages/hotelSelection/models';
import type { HotelSelectionEventTypes } from '../../../pages/hotelSelection/models';
import { HotelFilter } from '../../../modules/hotelFilters';
import type { HotelFilterUpdate } from '../../../modules/hotelFilters';
import type { RemoveHotelSelectionFiltersOrigin } from '../../models';
import { useRecipeStore } from '../recipe/recipeStore';
import { useTripStore } from '../trip/tripStore';
import {
  getAccommodationTypeText,
  getAmenitiesText,
  getBoardBasisTextNoIncluded,
} from '../../helpers/translation.helper';
import { sortAmenitiesFilterByPriority } from '../../models/hotelAmenity';

import { handleFilterUpdate } from './hotelFilters.logic';

export const useFiltersStore = defineStore('filtersStore', () => {
  const editingIngredientStore = useEditingIngredientStore();
  const statsigEventsStore = useStatsigEventsStore();
  const tripStore = useTripStore();
  const recipeStore = useRecipeStore();

  const DEFAULT_MAX_EXTENDED_FILTERS = 25;

  const editedHotelIngredient = ref<HotelIngredient | null>(null);

  const getHotelFreeCancellationFilter = computed<boolean>(() => {
    if (editedHotelIngredient.value === null) {
      setHotelIngredientForFiltering();
    }

    const filters = editedHotelIngredient.value?.filters;

    if (!filters) {
      return false;
    }

    return filters.selectedHotelRules.includes(
      AccommodationRule.FreeCancellation
    );
  });

  const getMealFitlerOptions = computed<AppMultiSelectOption<BoardBasisCode>[]>(
    () => {
      if (editedHotelIngredient.value === null) {
        setHotelIngredientForFiltering();
      }

      const allBoardBasisOptions: AppMultiSelectOption<BoardBasisCode>[] = [
        {
          id: BoardBasisCode.RoomOnly,
          label: getBoardBasisTextNoIncluded(BoardBasisCode.RoomOnly),
          selected: isBoardBasisSelected(BoardBasisCode.RoomOnly),
        },
        {
          id: BoardBasisCode.Breakfast,
          label: getBoardBasisTextNoIncluded(BoardBasisCode.Breakfast),
          selected: isBoardBasisSelected(BoardBasisCode.Breakfast),
        },
        {
          id: BoardBasisCode.HalfBoard,
          label: getBoardBasisTextNoIncluded(BoardBasisCode.HalfBoard),
          selected: isBoardBasisSelected(BoardBasisCode.HalfBoard),
        },
        {
          id: BoardBasisCode.FullBoard,
          label: getBoardBasisTextNoIncluded(BoardBasisCode.FullBoard),
          selected: isBoardBasisSelected(BoardBasisCode.FullBoard),
        },
        {
          id: BoardBasisCode.AlInclusive,
          label: getBoardBasisTextNoIncluded(BoardBasisCode.AlInclusive),
          selected: isBoardBasisSelected(BoardBasisCode.AlInclusive),
        },
      ];

      return allBoardBasisOptions;
    }
  );

  function updateHotelFilter(
    filterUpdate: HotelFilterUpdate,
    origin: RemoveHotelSelectionFiltersOrigin,
    updateOriginalIngredient = true
  ): void {
    const hostname = window.location.origin;
    const pathname = window.location.pathname.split(hostname)[0] ?? '';

    if (editedHotelIngredient.value === null) {
      setHotelIngredientForFiltering();
    }

    // Polygon ID is needed for tracking, case here should not exists for filters, but as we have possible undefined in parrent types, default fallback is 0
    // If we detect in statsig that we have cases where polygonId is 0, we should add a check for that case
    const polygonId: number | undefined = editedHotelIngredient.value
      ?.ingredientId
      ? tripStore.getMainPolygonId(editedHotelIngredient.value.ingredientId)
      : undefined;

    if (!editedHotelIngredient.value) {
      return;
    }
    const type = filterUpdate.type;
    const filterGroup = filterUpdate.type.split('.')[0] ?? '';
    let filterName = filterUpdate.type.split('.')[1] ?? '';
    let filterDetail =
      typeof filterUpdate.value !== 'boolean'
        ? filterUpdate.value.toString()
        : '';

    if (filterUpdate.type === HotelFilter.NeighborhoodPolygons) {
      filterName = filterUpdate.value;
      filterDetail =
        editedHotelIngredient.value.neighborhoodPolygons.find(
          (polygon) => polygon.id === filterUpdate.value
        )?.name ?? '';
    }
    filterUpdate.value
      ? handleEvents({
          type: HotelSelectionEventType.ApplyFilter,
          data: {
            filterName: filterName ?? '',
            filterGroup: filterGroup ?? '',
            filterDetail: filterDetail,
            polygonId,
            pathname,
            eventPlacement: origin,
          },
        })
      : handleEvents({
          type: HotelSelectionEventType.RemoveFilter,
          data: {
            filterName: filterName ?? '',
            filterGroup: filterGroup ?? '',
            filterDetail: filterDetail,
            polygonId,
            pathname,
            eventPlacement: origin,
          },
        });

    handleFilterUpdate(type, editedHotelIngredient.value, filterUpdate.value);

    if (
      updateOriginalIngredient &&
      editingIngredientStore.editingStayIngredient
    ) {
      editingIngredientStore.editingStayIngredient.filters =
        editedHotelIngredient.value?.filters;
    }
  }

  function getChangedHotelIngredient(): HotelIngredient | null {
    return editedHotelIngredient.value;
  }

  function clearEditedHotelIngredient(): void {
    editedHotelIngredient.value = null;
  }

  function setHotelIngredientForFiltering(): void {
    if (
      editingIngredientStore.editingStayIngredient &&
      isHotelIngredient(editingIngredientStore.editingStayIngredient)
    ) {
      editedHotelIngredient.value = cloneDeep(
        editingIngredientStore.editingStayIngredient
      );
    }
  }

  function hasActiveHotelFilters(): boolean {
    return editedHotelIngredient.value?.hasActiveFilters() ?? false;
  }

  function activeHotelFiltersLength(): number {
    return editedHotelIngredient.value?.getActiveFiltersCount() ?? 0;
  }

  function handleEvents(event: HotelSelectionEventTypes): void {
    switch (event.type) {
      case HotelSelectionEventType.Initialized:
        statsigEventsStore.logHotelSelectionInitiated(event.data);
        break;

      case HotelSelectionEventType.OpenFilters:
        statsigEventsStore.logHotelSelectionFilterOpened(event.data);
        break;

      case HotelSelectionEventType.CloseFilters:
        statsigEventsStore.logHotelSelectionFilterClosed(event.data);
        break;

      case HotelSelectionEventType.ApplyFilter:
        statsigEventsStore.logHotelSelectionFilterApplied(event.data);
        break;

      case HotelSelectionEventType.RemoveFilter:
        statsigEventsStore.logHotelSelectionFilterRemoved(event.data);
        break;

      case HotelSelectionEventType.ResetFilter:
        statsigEventsStore.logHotelSelectionFilterReset(event.data);
        break;

      default:
        break;
    }
  }

  function removeFilterFromTripOverview(
    hotelFilter: HotelFilter,
    ingredientId: IngredientId,
    neighborhoodFilterId?: string
  ): void {
    const hotelIngredient = recipeStore.getHotelIngredient(ingredientId);
    editingIngredientStore.setStayEditingIngredient(hotelIngredient);

    let filterUpdate: string | StarsRatingFilter | boolean;
    switch (hotelFilter) {
      case HotelFilter.RatingStars:
        filterUpdate = 0;
        break;
      case HotelFilter.HotelName:
        filterUpdate = '';
        break;
      case HotelFilter.NeighborhoodPolygons:
        filterUpdate = neighborhoodFilterId ?? '';
        break;
      default:
        filterUpdate = false;
        break;
    }
    handleFilterUpdate(hotelFilter, hotelIngredient, filterUpdate);
    editedHotelIngredient.value = null;
  }

  function resetHotelFilters(): void {
    editingIngredientStore.editingStayIngredient?.resetAllFilters();
    setHotelIngredientForFiltering();
  }

  function clearHotelMealFilters(): void {
    if (!editedHotelIngredient.value) {
      return;
    }
    editedHotelIngredient.value.filters.boardBasis = [];
  }

  function clearHotelAmenitiesFilters(): void {
    if (!editedHotelIngredient.value) {
      return;
    }
    editedHotelIngredient.value.filters.selectedAmenitiesFilters = [];
  }

  function clearHotelAccommodationTypeFilters(): void {
    if (!editedHotelIngredient.value) {
      return;
    }
    editedHotelIngredient.value.filters.selectedHotelTypes = [];
  }

  function clearNeighborhoodPolygonsFilters(): void {
    if (!editedHotelIngredient.value) {
      return;
    }
    editedHotelIngredient.value.filters.polygonIds = [];
  }

  function isBoardBasisSelected(boardBasis: BoardBasisCode): boolean {
    const filters = editedHotelIngredient.value?.filters;
    if (!filters) {
      return false;
    }

    const hasBoardBasis = (code: BoardBasisFilter): boolean =>
      filters.boardBasis.includes(code);

    switch (boardBasis) {
      case BoardBasisCode.RoomOnly:
        return hasBoardBasis(BoardBasisFilter.RoomOnly);
      case BoardBasisCode.Breakfast:
        return hasBoardBasis(BoardBasisFilter.Breakfast);
      case BoardBasisCode.HalfBoard:
        return hasBoardBasis(BoardBasisFilter.HalfBoard);
      case BoardBasisCode['HalfBoardPlus']:
        return hasBoardBasis(BoardBasisFilter.HalfBoard);
      case BoardBasisCode.FullBoard:
        return hasBoardBasis(BoardBasisFilter.FullBoard);
      case BoardBasisCode.AlInclusive:
        return hasBoardBasis(BoardBasisFilter.AllInclusive);
      default:
        exhaustiveGuard(boardBasis, 'Invalid Board Basis.');
    }
  }

  function isAmenitySelected(amenity: AmenitiesFilter): boolean {
    const filters = editedHotelIngredient.value?.filters;
    if (!filters) {
      return false;
    }

    const hasAmenity = (amenity: AmenitiesFilter): boolean =>
      filters.selectedAmenitiesFilters.includes(amenity);

    switch (amenity) {
      case AmenitiesFilter.AirConditioned:
        return hasAmenity(AmenitiesFilter.AirConditioned);
      case AmenitiesFilter.Gym:
        return hasAmenity(AmenitiesFilter.Gym);
      case AmenitiesFilter.Parking:
        return hasAmenity(AmenitiesFilter.Parking);
      case AmenitiesFilter.Pool:
        return hasAmenity(AmenitiesFilter.Pool);
      case AmenitiesFilter.Spa:
        return hasAmenity(AmenitiesFilter.Spa);
      case AmenitiesFilter.VehicleChargingStation:
        return hasAmenity(AmenitiesFilter.VehicleChargingStation);
      case AmenitiesFilter.Wifi:
        return hasAmenity(AmenitiesFilter.Wifi);
      default:
        exhaustiveGuard(amenity, 'Invalid Amenity.');
    }
  }

  function isAccommodationTypeSelected(
    accommodationType: AccommodationProductType
  ): boolean {
    const filters = editedHotelIngredient.value?.filters;
    if (!filters) {
      return false;
    }

    const hasAccommodationType = (
      accommodationType: AccommodationProductType
    ): boolean => filters.selectedHotelTypes.includes(accommodationType);

    switch (accommodationType) {
      case AccommodationType.Hotel:
        return hasAccommodationType(AccommodationType.Hotel);
      case AccommodationType.Apartment:
        return hasAccommodationType(AccommodationType.Apartment);
      case AccommodationType.HouseOrCottage:
        return hasAccommodationType(AccommodationType.HouseOrCottage);

      default:
        exhaustiveGuard(accommodationType, 'Invalid accommodation type.');
    }
  }

  function sortAmenitiesByExperimentPriority(
    amenities: AmenitiesFilter[]
  ): AmenitiesFilter[] {
    return sortAmenitiesFilterByPriority(amenities);
  }

  function getAmenitiesFitlerOptions(): AppMultiSelectOption<AmenitiesFilter>[] {
    const allAmenitiesOptions = [
      AmenitiesFilter.AirConditioned,
      AmenitiesFilter.Gym,
      AmenitiesFilter.Parking,
      AmenitiesFilter.Pool,
      AmenitiesFilter.Spa,
      AmenitiesFilter.VehicleChargingStation,
      AmenitiesFilter.Wifi,
    ];

    const amenitiesOrder =
      sortAmenitiesByExperimentPriority(allAmenitiesOptions);

    const amenitiesOptions: AppMultiSelectOption<AmenitiesFilter>[] =
      amenitiesOrder.map((amenity) => ({
        id: amenity,
        label: getAmenitiesText(amenity),
        selected: isAmenitySelected(amenity),
      }));

    return amenitiesOptions;
  }

  function getAccommodationTypeFitlerOptions(): AppMultiSelectOption<AccommodationProductType>[] {
    const allAccommodationTypeOptions: AppMultiSelectOption<AccommodationProductType>[] =
      [
        {
          id: AccommodationType.Hotel,
          label: getAccommodationTypeText(AccommodationType.Hotel),
          selected: isAccommodationTypeSelected(AccommodationType.Hotel),
        },
        {
          id: AccommodationType.Apartment,
          label: getAccommodationTypeText(AccommodationType.Apartment),
          selected: isAccommodationTypeSelected(AccommodationType.Apartment),
        },
        {
          id: AccommodationType.HouseOrCottage,
          label: getAccommodationTypeText(AccommodationType.HouseOrCottage),
          selected: isAccommodationTypeSelected(
            AccommodationType.HouseOrCottage
          ),
        },
      ];

    return allAccommodationTypeOptions;
  }

  watch(
    () =>
      (editingIngredientStore.editingStayIngredient as HotelIngredient)
        ?.neighborhoodPolygons,
    (newPolygons) => {
      if (editedHotelIngredient.value) {
        editedHotelIngredient.value.neighborhoodPolygons = [
          ...(newPolygons ?? []),
        ];
      }
    },
    { deep: true }
  );

  const getNeighborhoodPolygonsFilterOptions = computed<
    AppMultiSelectOption<string>[]
  >(() => {
    if (editedHotelIngredient.value === null) {
      setHotelIngredientForFiltering();
    }
    if (
      !editedHotelIngredient.value ||
      !editedHotelIngredient.value.neighborhoodPolygons
    ) {
      return [];
    }

    const allNeighborhoodPolygonsFilterOptions: AppMultiSelectOption<string>[] =
      [...editedHotelIngredient.value.neighborhoodPolygons]
        .sort((a, b) => b.score - a.score)
        .slice(0, DEFAULT_MAX_EXTENDED_FILTERS)
        .map((option) => ({
          id: option.id,
          label: option.name,
          selected: editedHotelIngredient.value?.filters.polygonIds.some(
            (polygonId) => polygonId === option.id
          ),
        }));
    return allNeighborhoodPolygonsFilterOptions;
  });

  return {
    DEFAULT_MAX_EXTENDED_FILTERS,
    getHotelFreeCancellationFilter,
    editedHotelIngredient,
    getMealFitlerOptions,
    updateHotelFilter,
    getChangedHotelIngredient,
    clearEditedHotelIngredient,
    removeFilterFromTripOverview,
    resetHotelFilters,
    hasActiveHotelFilters,
    activeHotelFiltersLength,
    setHotelIngredientForFiltering,
    isBoardBasisSelected,
    clearHotelMealFilters,
    getAmenitiesFitlerOptions,
    clearHotelAmenitiesFilters,
    clearHotelAccommodationTypeFilters,
    getAccommodationTypeFitlerOptions,
    clearNeighborhoodPolygonsFilters,
    getNeighborhoodPolygonsFilterOptions,
    sortAmenitiesByExperimentPriority,
  };
});
